import {
  capitalizeAllWords,
  convertDateToYYYYMMFormat,
  MonthYearDatePicker,
  PageWrapper,
  STATUSPAGE,
} from '@repo/ui';
import { useFormik } from 'formik';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useConsolidatedActivitiesByStudent } from 'presentation/app/modules/Reports/hooks/useConsolidatedActivitiesByStudent';
import { Card, Col, FormGroup, Input, Label, Spinner } from 'reactstrap';
import * as Yup from 'yup';

export const ConsolidatedActivitiesByStudent = () => {
  //Hooks
  const {
    loading,
    selectedSubmitBtn,
    filters,
    saveFilters,
    setSelectedSubmitBtn,
    requestGetConsolidatedActivitiesByStudent,
  } = useConsolidatedActivitiesByStudent();

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: filters?.entity || null,
      course: filters?.course || null,
      semester: filters?.semester || null,
      unit: filters?.unit || null,
      groups: filters?.groups || [],
      period: filters?.period || convertDateToYYYYMMFormat(new Date()),
      validation: filters?.validation || null,
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      period: Yup.date().required('Campo é obrigatório'),
      validation: Yup.number().nullable(),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        entity: values.entity?.value,
        course: values.course?.value,
        semester: values.semester?.value,
        curricularUnit: values.unit?.value,
        groups: values.groups.map((group) => group.value),
        period: values.period,
        validation: values.validation,
      };

      saveFilters({ ...values });

      switch (selectedSubmitBtn) {
        case 'export-pdf':
          requestGetConsolidatedActivitiesByStudent({
            ...valuesToSubmit,
            type: 0,
          });
          break;
        case 'export-excel':
          requestGetConsolidatedActivitiesByStudent({
            ...valuesToSubmit,
            type: 1,
          });
          break;
        default:
          break;
      }
    },
  });

  return (
    <PageWrapper title="Atividades Consolidadas">
      <div className="mb-4">
        <h4>Atividades Consolidadas</h4>
        <p className="text-muted mb-0">
          Visualize o consolidado mensal de atividades por estudante.
        </p>
      </div>
      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Col xl={2} className="d-flex flex-column">
                <Label>Período:</Label>
                <MonthYearDatePicker
                  validation={validation}
                  fieldName="period"
                />
              </Col>
              <Col className="d-flex flex-column">
                <Label>Validação:</Label>
                <div className="d-flex gap-4">
                  <FormGroup>
                    <Input
                      name="validation"
                      id="all"
                      type="radio"
                      checked={!validation.values.validation}
                      onChange={(e) => {
                        if (e.target.checked) {
                          validation.handleChange({
                            target: {
                              name: 'validation',
                              value: null,
                            },
                          });
                        }
                      }}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="all" className="ms-1 form-check-label">
                      Todas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="validated"
                      type="radio"
                      value={1}
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="validated"
                      className="ms-1 form-check-label"
                    >
                      Validadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="rejected"
                      type="radio"
                      value={2}
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="rejected" className="ms-1 form-check-label">
                      Rejeitadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="not-validated"
                      type="radio"
                      value={3}
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="not-validated"
                      className="ms-1 form-check-label"
                      onChange={validation.handleChange}
                    >
                      Pendentes
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </>
          }
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end me-3">
              <button
                type="submit"
                id="export-pdf"
                onClick={() => {
                  setSelectedSubmitBtn('export-pdf');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={
                  loading === STATUSPAGE.LOADING &&
                  selectedSubmitBtn === 'export-pdf'
                }
              >
                {loading === STATUSPAGE.LOADING &&
                selectedSubmitBtn === 'export-pdf' ? (
                  <Spinner size="sm" className="ms-1">
                    {['Carregando...']}
                  </Spinner>
                ) : (
                  <>
                    Visualizar em PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </button>
              <button
                id="export-excel"
                type="submit"
                onClick={() => {
                  setSelectedSubmitBtn('export-excel');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={
                  loading === STATUSPAGE.LOADING &&
                  selectedSubmitBtn === 'export-excel'
                }
              >
                {loading === STATUSPAGE.LOADING &&
                selectedSubmitBtn === 'export-excel' ? (
                  <Spinner size="sm" className="ms-1">
                    {['Carregando...']}
                  </Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ri-file-excel-2-line mx-2"></i>
                  </>
                )}
              </button>
            </div>
          }
        />
      </Card>
    </PageWrapper>
  );
};
